import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../services/AuthService';
import { RideService } from '../services/RideService';
import { Passenger, Ride } from '../types';
import './Dashboard.css';

const PassengerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [passenger, setPassenger] = useState<Passenger | null>(null);
  const [rides, setRides] = useState<Ride[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const currentUser = AuthService.getCurrentUser();
        if (!currentUser) {
          navigate('/passenger-login');
          return;
        }

        const userData = await AuthService.getUserData(currentUser.uid);
        if (!userData || userData.userType !== 'PASSENGER') {
          navigate('/passenger-login');
          return;
        }

        setPassenger(userData as Passenger);
        
        // Yolcunun yolculuklarını getir
        const passengerRides = await RideService.getPassengerRides(currentUser.uid);
        setRides(passengerRides);
      } catch (error) {
        console.error('Dashboard verisi yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await AuthService.logout();
      navigate('/');
    } catch (error) {
      console.error('Çıkış hatası:', error);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  if (!passenger) {
    return <div>Kullanıcı bulunamadı</div>;
  }

  // Membership kontrolü ve varsayılan değer atama
  if (!passenger.membership) {
    passenger.membership = {
      type: 'FREE' as any,
      amount: 0,
      paid: false,
      features: ['Temel taksi çağırma', 'Standart müşteri desteği'],
      validUntil: null
    };
  }

  const completedRides = rides.filter(ride => ride.status === 'COMPLETED');
  const totalSpent = completedRides.reduce((sum, ride) => sum + (ride.fare || 0), 0);
  const averageRidefare = completedRides.length > 0 ? totalSpent / completedRides.length : 0;
  const thisMonthRides = rides.filter(ride => {
    const rideDate = new Date(ride.createdAt);
    const now = new Date();
    return rideDate.getMonth() === now.getMonth() && rideDate.getFullYear() === now.getFullYear();
  });

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="logo">
            <h2>Taxicim Yolcu</h2>
          </div>
          <div className="user-info">
            <span>Hoş geldiniz, {passenger.firstName} {passenger.lastName}</span>
            <div className="membership-badge">
              <span className={`badge ${passenger.membership?.type?.toLowerCase() || 'free'}`}>
                {passenger.membership?.type === 'PREMIUM' ? '👑 Premium' : '🎯 Ücretsiz'}
              </span>
            </div>
            <button onClick={handleLogout} className="logout-btn">
              Çıkış
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <nav className="dashboard-nav">
          <button 
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => setActiveTab('overview')}
          >
            📊 Genel Bakış
          </button>
          <button 
            className={activeTab === 'membership' ? 'active' : ''}
            onClick={() => setActiveTab('membership')}
          >
            💎 Üyelik Detayları
          </button>
          <button 
            className={activeTab === 'rides' ? 'active' : ''}
            onClick={() => setActiveTab('rides')}
          >
            🚗 Yolculuklar
          </button>
          <button 
            className={activeTab === 'profile' ? 'active' : ''}
            onClick={() => setActiveTab('profile')}
          >
            👤 Profil
          </button>
        </nav>

        <main className="dashboard-main">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <h3>Genel Bakış</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <h4>Toplam Yolculuk</h4>
                  <p className="stat-number">{passenger.totalRides || 0}</p>
                  <p className="stat-sub">Tüm zamanlar</p>
                </div>
                <div className="stat-card">
                  <h4>Bu Ay</h4>
                  <p className="stat-number">{thisMonthRides.length}</p>
                  <p className="stat-sub">yolculuk</p>
                </div>
                <div className="stat-card">
                  <h4>Toplam Harcama</h4>
                  <p className="stat-number">₺{totalSpent.toFixed(2)}</p>
                  <p className="stat-sub">Tamamlanan yolculuklar</p>
                </div>
                <div className="stat-card">
                  <h4>Ortalama Ücret</h4>
                  <p className="stat-number">₺{averageRidefare.toFixed(2)}</p>
                  <p className="stat-sub">Yolculuk başına</p>
                </div>
                <div className="stat-card">
                  <h4>Puan</h4>
                  <p className="stat-number">{(passenger.rating || 0).toFixed(1)} ⭐</p>
                  <p className="stat-sub">5 üzerinden</p>
                </div>
                <div className="stat-card">
                  <h4>Bakiye</h4>
                  <p className="stat-number">₺{(passenger.balance || 0).toFixed(2)}</p>
                  <p className="stat-sub">Mevcut bakiye</p>
                </div>
              </div>

              <div className="quick-membership-info">
                <h4>Üyelik Durumu</h4>
                <div className={`membership-status ${passenger.membership?.type?.toLowerCase() || 'free'}`}>
                  <div className="membership-header">
                    <h5>
                      {passenger.membership?.type === 'PREMIUM' ? '👑 Premium Üyelik' : '🎯 Ücretsiz Üyelik'}
                    </h5>
                    <span className={`status-badge ${passenger.membership?.paid ? 'paid' : 'unpaid'}`}>
                      {passenger.membership?.paid ? 'Aktif' : 'Ödeme Bekliyor'}
                    </span>
                  </div>
                  <div className="membership-preview">
                    <p><strong>Avantajlar:</strong> {passenger.membership?.features?.slice(0, 3).join(', ') || 'Temel hizmetler'}</p>
                    {passenger.membership?.validUntil && (
                      <p><strong>Geçerlilik:</strong> {new Date(passenger.membership.validUntil).toLocaleDateString('tr-TR')}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'membership' && (
            <div className="membership-tab">
              <h3>Üyelik Detayları</h3>
              <div className={`membership-detail-card ${passenger.membership?.type?.toLowerCase() || 'free'}`}>
                <div className="membership-badge-large">
                  {passenger.membership?.type === 'PREMIUM' ? '👑' : '🎯'}
                </div>
                <h4>{passenger.membership?.type === 'PREMIUM' ? 'Premium Üyelik' : 'Ücretsiz Üyelik'}</h4>
                
                <div className="membership-info-grid">
                  <div className="info-item">
                    <span className="label">Ücret:</span>
                    <span className="value">₺{passenger.membership?.amount || 0}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Durum:</span>
                    <span className={`value ${passenger.membership?.paid ? 'paid' : 'unpaid'}`}>
                      {passenger.membership?.paid ? '✅ Ödendi' : '❌ Ödeme Bekliyor'}
                    </span>
                  </div>
                  {passenger.membership?.paidAt && (
                    <div className="info-item">
                      <span className="label">Ödeme Tarihi:</span>
                      <span className="value">{new Date(passenger.membership.paidAt).toLocaleDateString('tr-TR')}</span>
                    </div>
                  )}
                  {passenger.membership?.paymentMethod && (
                    <div className="info-item">
                      <span className="label">Ödeme Yöntemi:</span>
                      <span className="value">{passenger.membership.paymentMethod}</span>
                    </div>
                  )}
                  {passenger.membership?.validUntil && (
                    <div className="info-item">
                      <span className="label">Geçerlilik:</span>
                      <span className="value">{new Date(passenger.membership.validUntil).toLocaleDateString('tr-TR')}</span>
                    </div>
                  )}
                </div>

                <div className="features-section">
                  <h5>🎁 Üyelik Avantajları</h5>
                  <div className="features-list">
                    {passenger.membership?.features?.map((feature, index) => (
                      <div key={index} className="feature-item">
                        <span className="check">✅</span>
                        <span>{feature}</span>
                      </div>
                    )) || []}
                  </div>
                </div>

                {passenger.membership?.type === 'FREE' && (
                  <div className="upgrade-section">
                    <h5>🚀 Premium'a Geçin</h5>
                    <p>Premium üyelikle daha fazla avantaj elde edin!</p>
                    <ul>
                      <li>✨ Öncelikli taksi çağırma</li>
                      <li>🎧 Premium müşteri desteği</li>
                      <li>🌟 Özel indirimler</li>
                      <li>✈️ Havaalanı önceliği</li>
                      <li>🎁 Biriken puanlarla ücretsiz yolculuk</li>
                    </ul>
                    <button className="upgrade-btn">Premium'a Geç</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'rides' && (
            <div className="rides-tab">
              <h3>Yolculuk Geçmişi</h3>
              <div className="rides-stats">
                <div className="stat">
                  <span className="number">{completedRides.length}</span>
                  <span className="label">Tamamlanan</span>
                </div>
                <div className="stat">
                  <span className="number">{rides.filter(r => r.status === 'CANCELLED').length}</span>
                  <span className="label">İptal Edilen</span>
                </div>
                <div className="stat">
                  <span className="number">₺{totalSpent.toFixed(2)}</span>
                  <span className="label">Toplam Harcama</span>
                </div>
              </div>
              
              <div className="rides-list">
                {rides.length > 0 ? (
                  rides.map((ride) => (
                    <div key={ride.id} className="ride-card">
                      <div className="ride-info">
                        <div className="ride-locations">
                          <p className="pickup">
                            <span className="icon">📍</span>
                            <strong>Başlangıç:</strong> {ride.pickupLocation.address}
                          </p>
                          <p className="dropoff">
                            <span className="icon">🎯</span>
                            <strong>Varış:</strong> {ride.dropoffLocation.address}
                          </p>
                        </div>
                        <div className="ride-meta">
                          <p><span className="icon">📅</span> {new Date(ride.createdAt).toLocaleDateString('tr-TR')}</p>
                          <p><span className="icon">🕐</span> {new Date(ride.createdAt).toLocaleTimeString('tr-TR', {hour: '2-digit', minute: '2-digit'})}</p>
                          {ride.distance && <p><span className="icon">📏</span> {ride.distance.toFixed(2)} km</p>}
                          {ride.duration && <p><span className="icon">⏱️</span> {Math.round(ride.duration)} dk</p>}
                        </div>
                      </div>
                      <div className="ride-details">
                        <p className={`status ${ride.status.toLowerCase()}`}>
                          {ride.status === 'COMPLETED' ? '✅ Tamamlandı' :
                           ride.status === 'CANCELLED' ? '❌ İptal Edildi' :
                           ride.status === 'IN_PROGRESS' ? '🚗 Devam Ediyor' :
                           ride.status === 'ACCEPTED' ? '🤝 Kabul Edildi' :
                           ride.status === 'REQUESTED' ? '⏳ Talep Edildi' : ride.status}
                        </p>
                        <p className="fare">💰 ₺{ride.fare.toFixed(2)}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-rides">
                    <span className="icon">🚗</span>
                    <p>Henüz yolculuk bulunamadı.</p>
                    <p>İlk yolculuğunuzu mobil uygulamadan gerçekleştirin!</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="profile-tab">
              <h3>Profil Bilgileri</h3>
              <div className="profile-info">
                <div className="info-section">
                  <h4>👤 Kişisel Bilgiler</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="label">Ad:</span>
                      <span className="value">{passenger.firstName}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">Soyad:</span>
                      <span className="value">{passenger.lastName}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">E-posta:</span>
                      <span className="value">{passenger.email}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">Telefon:</span>
                      <span className="value">{passenger.phone}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">Doğrulama:</span>
                      <span className={`value ${passenger.isVerified ? 'verified' : 'unverified'}`}>
                        {passenger.isVerified ? '✅ Doğrulandı' : '⏳ Beklemede'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="info-section">
                  <h4>📍 Favori Konumlar</h4>
                  {passenger.favoriteLocations && passenger.favoriteLocations.length > 0 ? (
                    <div className="favorite-locations">
                      {passenger.favoriteLocations.map((location, index) => (
                        <div key={index} className="location-item">
                          <span className="location-icon">📍</span>
                          <div className="location-details">
                            <p className="location-name">{location.name || `Konum ${index + 1}`}</p>
                            <p className="location-address">{location.address}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-locations">
                      <span className="icon">📍</span>
                      <p>Henüz favori konum eklenmemiş.</p>
                      <p>Mobil uygulamadan favori konumlarınızı ekleyebilirsiniz.</p>
                    </div>
                  )}
                </div>

                <div className="info-section">
                  <h4>📊 Hesap İstatistikleri</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="label">Kayıt Tarihi:</span>
                      <span className="value">{new Date(passenger.createdAt).toLocaleDateString('tr-TR')}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">Son Güncelleme:</span>
                      <span className="value">{new Date(passenger.updatedAt).toLocaleDateString('tr-TR')}</span>
                    </div>
                    <div className="info-item">
                      <span className="label">Kullanıcı ID:</span>
                      <span className="value small">{passenger.id}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default PassengerDashboard; 